import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spin, Alert } from 'antd';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  AggregatesDirective,
  AggregateDirective,
  AggregateColumnsDirective,
  AggregateColumnDirective,
  Inject,
  Aggregate,
} from '@syncfusion/ej2-react-grids';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import type { RootState } from '@/store';
import {
  getDashboardMetric13,
  type DashboardMetric13ProcessOwnerCompletion,
} from '@/services/dashboardService';

const GridWrapper = styled.div`
  /* ... your existing CSS ... */

  /* Ensure consistent sizing across header/body/footer */
  .e-grid *,
  .e-grid *::before,
  .e-grid *::after {
    box-sizing: border-box;
  }

  /* Make footer (aggregate) cells match row cell padding/borders */
  .e-grid .e-summaryrow .e-summarycell,
  .e-grid .e-summaryrow .e-summarycontent,
  .e-grid .e-summarycell {
    padding: 12px 16px !important; /* match your .e-rowcell */
    border-color: #e5e7eb !important; /* match */
    vertical-align: middle;
  }

  /* Optional: align footer text like the grid cells */
  .e-grid .e-summaryrow .e-summarycell {
    font-size: 14px;
    color: #1f2937;
  }
`;

export type EX1ProcessOwnercertificationCompletionStatusGridProps = {
  title?: string;
  minHeight?: number;
};

const formatPercent = (value: number | null | undefined) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '–';
  // If API ever returns 0..1, this keeps it safe:
  const pct = value <= 1 ? value * 100 : value;
  return `${Math.round(pct)}%`;
};

const averageField = (rows: any[], field: string) => {
  const nums = (rows || [])
    .map(r => r?.[field])
    .filter(v => typeof v === 'number' && !Number.isNaN(v));

  if (nums.length === 0) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

export const EX1ProcessOwnercertificationCompletionStatusGrid: React.FC<
  EX1ProcessOwnercertificationCompletionStatusGridProps
> = ({
  title = 'Process Owner Certification Completion Status',
  minHeight = 220,
}) => {
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [rows, setRows] = useState<DashboardMetric13ProcessOwnerCompletion[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRows([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric13(annualReportId);
        if (!isMounted) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRows([]);
        setErrorMessage(
          err?.message || err?.Message || 'Failed to load Metric 13.',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const dataSource = useMemo(() => {
    // Keep a stable key for the grid
    return rows.map(r => ({
      ...r,
      id: r.processOwnerId,
    }));
  }, [rows]);

  // ✅ IMPORTANT: type these as (props: any) => number to satisfy Syncfusion
  // Syncfusion passes an object where props.result is the current rows.
  const labelAggregate = (_props: any): string => 'Total Completion';

  const q1Aggregate = (props: any): number =>
    averageField(props?.result || [], 'quarterOneCompletionPercent');
  const q2Aggregate = (props: any): number =>
    averageField(props?.result || [], 'quarterTwoCompletionPercent');
  const q3Aggregate = (props: any): number =>
    averageField(props?.result || [], 'quarterThreeCompletionPercent');
  const q4Aggregate = (props: any): number =>
    averageField(props?.result || [], 'quarterFourCompletionPercent');

  // Footer templates
  const footerLabelTemplate = (_props: any) => {
    // For Custom type, Syncfusion usually exposes the value as props.Custom
    return <div style={{ fontWeight: 600 }}>Total Completion</div>;
  };

  const footerPercentTemplate = (props: any) => {
    return (
      <div style={{ fontWeight: 600 }}>{formatPercent(props?.Custom)}</div>
    );
  };

  return (
    <Card title={title}>
      {loading ? (
        <div
          style={{
            minHeight, // ✅ uses min height only for loading
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin />
        </div>
      ) : errorMessage ? (
        <div style={{ padding: 16 }}>
          <Alert
            type='error'
            message='Dashboard Metric 13 Error'
            description={errorMessage}
            showIcon
          />
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <GridWrapper>
            <GridComponent
              dataSource={dataSource}
              height='auto' // ✅ key change
              width='100%' // ✅ key change
              allowSorting={false}
              allowFiltering={false}
              enableHover={true}
              rowHeight={44}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field='processOwnerName'
                  headerText='Process Owner'
                  width={320} // ✅ pixel width
                  textAlign='Left'
                  clipMode='EllipsisWithTooltip'
                />
                <ColumnDirective
                  field='quarterOneCompletionPercent'
                  headerText='Q1'
                  width={120}
                  textAlign='Center'
                  template={(row: any) =>
                    formatPercent(row?.quarterOneCompletionPercent)
                  }
                />
                <ColumnDirective
                  field='quarterTwoCompletionPercent'
                  headerText='Q2'
                  width={120}
                  textAlign='Center'
                  template={(row: any) =>
                    formatPercent(row?.quarterTwoCompletionPercent)
                  }
                />
                <ColumnDirective
                  field='quarterThreeCompletionPercent'
                  headerText='Q3'
                  width={120}
                  textAlign='Center'
                  template={(row: any) =>
                    formatPercent(row?.quarterThreeCompletionPercent)
                  }
                />
                <ColumnDirective
                  field='quarterFourCompletionPercent'
                  headerText='Q4'
                  width={120}
                  textAlign='Center'
                  template={(row: any) =>
                    formatPercent(row?.quarterFourCompletionPercent)
                  }
                />
              </ColumnsDirective>

              <AggregatesDirective>
                <AggregateDirective>
                  <AggregateColumnsDirective>
                    <AggregateColumnDirective
                      field='processOwnerName'
                      type='Custom'
                      customAggregate={labelAggregate}
                      footerTemplate={footerLabelTemplate}
                    />
                    <AggregateColumnDirective
                      field='quarterOneCompletionPercent'
                      type='Custom'
                      customAggregate={q1Aggregate}
                      footerTemplate={footerPercentTemplate}
                    />
                    <AggregateColumnDirective
                      field='quarterTwoCompletionPercent'
                      type='Custom'
                      customAggregate={q2Aggregate}
                      footerTemplate={footerPercentTemplate}
                    />
                    <AggregateColumnDirective
                      field='quarterThreeCompletionPercent'
                      type='Custom'
                      customAggregate={q3Aggregate}
                      footerTemplate={footerPercentTemplate}
                    />
                    <AggregateColumnDirective
                      field='quarterFourCompletionPercent'
                      type='Custom'
                      customAggregate={q4Aggregate}
                      footerTemplate={footerPercentTemplate}
                    />
                  </AggregateColumnsDirective>
                </AggregateDirective>
              </AggregatesDirective>

              <Inject services={[Aggregate]} />
            </GridComponent>
          </GridWrapper>
        </div>
      )}
    </Card>
  );
};

export default EX1ProcessOwnercertificationCompletionStatusGrid;
